import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Advisor } from '@minutes-system/backend/src/api/advisors/schema';
import { advisorsQueryAtom, buildAdvisorsQueryKey } from '../../../../states/advisors';
import calculateMaxPage from '../../../../lib/calculateMaxPage';
import { AdvisorSearchCondition } from '../types';

interface UseAdvisorsInterface {
  page: number;
  setPage: (page: number) => void;
  advisors: Advisor[];
  total: number;
  pageSize: number;
  searchValues: AdvisorSearchCondition;
  handleSearch: (values: AdvisorSearchCondition) => void;
  handleClear: () => void;
}

// backend の 1ページ件数 (service.ts の PER_PAGE と揃える)
const PAGE_SIZE = 10;

const useAdvisors = (): UseAdvisorsInterface => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const page = Number.isInteger(pageParam) && pageParam >= 1 ? pageParam : 1;

  const searchValues: AdvisorSearchCondition = {
    name: searchParams.get('name') ?? undefined,
    phone: searchParams.get('phone') ?? undefined,
  };

  const queryKey = buildAdvisorsQueryKey({ page, ...searchValues });
  const data = useAtomValue(advisorsQueryAtom(queryKey));

  // pageパラメータが無効、または最大ページ数を超えている場合は1ページ目にリダイレクト
  const maxPage = calculateMaxPage(data.count, PAGE_SIZE);
  const isInvalidPage = !Number.isInteger(pageParam) || pageParam < 1 || pageParam > maxPage;
  useEffect(() => {
    if (isInvalidPage) {
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }
  }, [isInvalidPage, searchParams, setSearchParams]);

  const setPage = useCallback(
    (next: number) => {
      if (next < 1) {
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.set('page', String(next));
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(
    (values: AdvisorSearchCondition) => {
      const params = new URLSearchParams();
      if (values.name) {
        params.set('name', values.name);
      }
      if (values.phone) {
        params.set('phone', values.phone);
      }
      params.set('page', '1');
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleClear = useCallback(() => {
    setSearchParams({ page: '1' });
  }, [setSearchParams]);

  return {
    page,
    setPage,
    advisors: data.advisors,
    total: data.count,
    pageSize: PAGE_SIZE,
    searchValues,
    handleSearch,
    handleClear,
  };
};

export default useAdvisors;
